import { z } from "zod";
import {
  AccessInquiryRequest,
  CallbackInquiryRequest,
  InquiryCreateRequest,
  SupportInquiryRequest,
  Uuid,
  WorkflowInquiryRequest,
  type InquiryKind,
} from "@ai-platform/contracts";
import type { FormField } from "@pixelpoint/astro-core/schemas/content";

/*
 * The inquiry form's definition (UC-30, ADR-093): its fields per kind as `FormField[]`, the way
 * astro-core's form framework defines forms (`docs/forms.md` there), and the values checked with
 * the contract's schema of the kind (07 §5: forms check with the contract). The component
 * `InquiryForm.vue` renders and sends; everything it decides lives here, so it is tested here.
 */

/** A product the sender may pick; the app passes what it knows (the portal: the catalogue). */
export type InquiryProductOption = { id: string; name: string };

/**
 * What the app answers after sending: the form shows its thanks, or the app's text of the failure.
 * A `400` names the fields the API refused (`fields`, from `refusedFields`); the form marks those it
 * shows with their own error and says so instead of the app's text.
 */
export type InquirySendResult = { ok: true } | { ok: false; message: string; fields?: string[] };

/** The app's way to the API; the form knows neither address nor client (ADR-093 point 9). */
export type InquirySend = (body: InquiryCreateRequest) => Promise<InquirySendResult>;

/** vue-i18n's `t`, narrowed to what the definition needs. */
export type Translate = (key: string) => string;

/**
 * The kinds this form asks for. The application for the beta (`application`, ADR-143) asks far
 * more and has a form of its own (`ApplicationForm.vue`).
 */
export type FormInquiryKind = Exclude<InquiryKind, "application">;

/** The issues of a `400 validation_failed`: the API names each one's field in `path` (07 §1 K-04). */
const ValidationIssues = z.array(z.object({ path: z.array(z.union([z.string(), z.number()])) }));

/**
 * The fields a `400 validation_failed` refused, by their name in the body (`path[0]`), each once;
 * details it cannot read name none. Both apps read the answer's `details` with it.
 */
export function refusedFields(details: unknown): string[] {
  const issues = ValidationIssues.safeParse(details);
  if (!issues.success) return [];
  return [...new Set(issues.data.flatMap((issue) => (typeof issue.path[0] === "string" ? [issue.path[0]] : [])))];
}

/** The product field's value for "no product in particular": a select item cannot carry "". */
export const NO_PRODUCT = "none";

/**
 * Where the sender comes from, when that says more than the kind (design prototype REQ_LEADS):
 * asking for a talk (the website), for an offer on a product that is not ordered online, or for
 * access after a login refused for an instance not enabled (`instance_not_enabled`, ADR-093
 * point 8). Each entry has a title, an intro and a message placeholder in the texts.
 */
export const INQUIRY_LEADS = ["talk", "product", "instance"] as const;
export type InquiryLead = (typeof INQUIRY_LEADS)[number];

/**
 * The heading and intro the app shows above the form: the entry's when there is one, a general
 * one for a choice of kinds, else the kind's.
 */
export function inquiryHeading(
  kinds: readonly [FormInquiryKind, ...FormInquiryKind[]],
  t: Translate,
  lead?: InquiryLead,
): { title: string; intro: string } {
  if (lead) return { title: t(`inquiryForm.lead.${lead}.title`), intro: t(`inquiryForm.lead.${lead}.intro`) };
  const key = kinds.length > 1 ? "several" : kinds[0];
  return { title: t(`inquiryForm.title.${key}`), intro: t(`inquiryForm.intro.${key}`) };
}

/** The send button and the thanks of a kind: a callback is asked for and promised (B-132, B-133). */
export function inquiryButtonTexts(kind: FormInquiryKind, t: Translate): { submit: string; sent: string } {
  const variant = kind === "callback" ? "callback" : "default";
  return { submit: t(`inquiryForm.submit.${variant}`), sent: t(`inquiryForm.sent.${variant}`) };
}

const product = z.preprocess((value) => (value === NO_PRODUCT || value === "" ? undefined : value), Uuid.optional());

/**
 * What the sender fills in, per kind: the contract's body without what the form adds itself
 * (`kind`; honeypot and `startedAt` for `access`), and the product's "none" read as no product.
 */
export const inquiryValueSchemas = {
  access: AccessInquiryRequest.omit({ kind: true, website: true, startedAt: true }).extend({ productId: product }),
  support: SupportInquiryRequest.omit({ kind: true }).extend({ productId: product }),
  callback: CallbackInquiryRequest.omit({ kind: true }).extend({ productId: product }),
  workflow: WorkflowInquiryRequest.omit({ kind: true }).extend({ productId: product }),
} satisfies Record<FormInquiryKind, z.ZodObject>;

/** The text key of a field's error; name and company can only be too long. */
export function inquiryErrorKey(identifier: string): string {
  return identifier === "name" || identifier === "company" ? "inquiryForm.tooLong" : `inquiryForm.${identifier}.error`;
}

/**
 * The fields of a kind, in the order the form shows them. `access` asks who is writing; the
 * kinds of a customer's session take name, e-mail and company from the session (ADR-093 point 3).
 * The product field appears only when the app offers products. The message is labelled per kind
 * and its placeholder follows the entry, else the kind.
 */
export function inquiryFields(
  kind: FormInquiryKind,
  t: Translate,
  products: InquiryProductOption[],
  lead?: InquiryLead,
): FormField[] {
  // the form marks the fields one may leave empty with "(optional)" after the label (InquiryForm)
  const field = (identifier: string, type: FormField["type"], required: boolean, extra: Partial<FormField> = {}) => ({
    name: identifier,
    identifier,
    type,
    required,
    label: extra.label ?? t(`inquiryForm.${identifier}.label`),
    ...extra,
  });
  const productField = products.length
    ? [
        field("productId", "select", false, {
          options: [
            { value: NO_PRODUCT, label: t("inquiryForm.productId.none") },
            ...products.map((option) => ({ value: option.id, label: option.name })),
          ],
        }),
      ]
    : [];
  const message = field("message", "textarea", true, {
    label: t(`inquiryForm.message.label.${kind}`),
    placeholder: t(lead ? `inquiryForm.lead.${lead}.placeholder` : `inquiryForm.message.placeholder.${kind}`),
  });

  switch (kind) {
    case "access":
      return [
        field("name", "text", false),
        field("email", "email", true),
        field("company", "text", false, { placeholder: t("inquiryForm.company.placeholder") }),
        ...productField,
        message,
      ];
    case "callback":
      return [
        ...productField,
        field("phone", "tel", true, { placeholder: t("inquiryForm.phone.placeholder") }),
        message,
      ];
    default: // support and workflow (handled alike in the alpha release, ADR-093 B.1)
      return [...productField, message];
  }
}

/** What the form adds to an `access` body: the honeypot as typed and when the form was shown (ADR-050). */
export type InquiryGuard = { website: string; startedAt: string };

/**
 * The body for `POST /api/inquiries` from the checked values, or nothing when it does not hold
 * the contract (a filled honeypot, or a form sent before it was shown). Empty optional fields are
 * left out. Without a product field, the preselected product goes along as it is.
 */
export function inquiryBody(
  kind: FormInquiryKind,
  values: Record<string, unknown>,
  guard: InquiryGuard,
  presetProductId?: string,
): InquiryCreateRequest | undefined {
  const filled = Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined && value !== ""));
  const productId = "productId" in values ? filled.productId : presetProductId;
  const body = {
    ...filled,
    ...(productId === undefined ? {} : { productId }),
    kind,
    ...(kind === "access" ? { website: guard.website, startedAt: guard.startedAt } : {}),
  };
  const parsed = InquiryCreateRequest.safeParse(body);
  return parsed.success ? parsed.data : undefined;
}
